
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DividendsAdjustmentExecuteResult struct
 */
class DividendsAdjustmentExecuteResult {
  /**
   * Copy struct (shallow copy)
   * @this {!DividendsAdjustmentExecuteResult}
   * @param {!DividendsAdjustmentExecuteResult} other Other struct
   * @returns {!DividendsAdjustmentExecuteResult} This struct
   */
  copy (other) {
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DividendsAdjustmentExecuteResult}
   * @returns {!DividendsAdjustmentExecuteResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DividendsAdjustmentExecuteResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DividendsAdjustmentExecuteResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DividendsAdjustmentExecuteResult}
   * @param {!DividendsAdjustmentExecuteResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DividendsAdjustmentExecuteResult)) {
      throw new TypeError('Instance of DividendsAdjustmentExecuteResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DividendsAdjustmentExecuteResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DividendsAdjustmentExecuteResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DividendsAdjustmentExecuteResult} other Object value
   * @returns {!DividendsAdjustmentExecuteResult} Created struct
   */
  static fromObject (other) {
    return new DividendsAdjustmentExecuteResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DividendsAdjustmentExecuteResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DividendsAdjustmentExecuteResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DividendsAdjustmentExecuteResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 12036
  }
}

export { DividendsAdjustmentExecuteResult };

/**
 * Fast Binary Encoding DividendsAdjustmentExecuteResult field model
 */
class FieldModelDividendsAdjustmentExecuteResult extends fbe.FieldModel {
  /**
   * Get the field size
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDividendsAdjustmentExecuteResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 12036
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @param {!DividendsAdjustmentExecuteResult} fbeValue Default value, defaults is new DividendsAdjustmentExecuteResult()
   * @returns {!DividendsAdjustmentExecuteResult} DividendsAdjustmentExecuteResult value
   */
  get (fbeValue = new DividendsAdjustmentExecuteResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @param {!DividendsAdjustmentExecuteResult} fbeValue DividendsAdjustmentExecuteResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @param {!DividendsAdjustmentExecuteResult} fbeValue DividendsAdjustmentExecuteResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDividendsAdjustmentExecuteResult}
   * @param {DividendsAdjustmentExecuteResult} fbeValue DividendsAdjustmentExecuteResult value
   */
  setFields (fbeValue) {
  }
}

export { FieldModelDividendsAdjustmentExecuteResult };

/**
 * Fast Binary Encoding DividendsAdjustmentExecuteResult model
 */
class DividendsAdjustmentExecuteResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDividendsAdjustmentExecuteResult(this.buffer, 4)
  }

  /**
   * Get the DividendsAdjustmentExecuteResult model
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @returns {!FieldModelDividendsAdjustmentExecuteResult} model DividendsAdjustmentExecuteResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DividendsAdjustmentExecuteResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDividendsAdjustmentExecuteResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @param {!DividendsAdjustmentExecuteResult} value DividendsAdjustmentExecuteResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @param {!DividendsAdjustmentExecuteResult} value DividendsAdjustmentExecuteResult value, defaults is new DividendsAdjustmentExecuteResult()
   * @return {!object} Deserialized DividendsAdjustmentExecuteResult value and its size
   */
  deserialize (value = new DividendsAdjustmentExecuteResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DividendsAdjustmentExecuteResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DividendsAdjustmentExecuteResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DividendsAdjustmentExecuteResultModel}
   * @param {!number} prev Previous DividendsAdjustmentExecuteResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DividendsAdjustmentExecuteResultModel };
